import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { container } from "tsyringe";
import { AnalyticsService } from "../../services/analytics";

export enum CompressionQuality {
    low = 'low',
    medium = 'medium',
    high = 'high'
}

export interface PdfFile {
    objectUrl: string;
    name: string;
    file: File;
    originalSize: number;
    compressedSize?: number;
    compressionRatio?: number;
}

export interface PdfCompressionState {
    pdfFile: PdfFile | null;
    compressionQuality: CompressionQuality;
    compressionInProgress: boolean;
    error: string | null;
}

const initialState: PdfCompressionState = {
    pdfFile: null,
    compressionQuality: CompressionQuality.medium,
    compressionInProgress: false,
    error: null,
};

export const PdfCompressionSlice = createSlice({
    name: 'pdfCompression',
    initialState,
    reducers: {
        setCompressionQuality: (state, action: PayloadAction<CompressionQuality>) => {
            state.compressionQuality = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        resetState: (state) => {
            state.pdfFile = null;
            state.compressionInProgress = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(addPdfFileAsync.fulfilled, (state, action) => {
                if (action.payload) {
                    state.pdfFile = action.payload;
                    state.error = null;
                }
            })
            .addCase(addPdfFileAsync.rejected, (state, action) => {
                state.error = action.error.message || 'Failed to load PDF file';
            })
            .addCase(compressPdfAsync.pending, (state) => {
                state.compressionInProgress = true;
                state.error = null;
            })
            .addCase(compressPdfAsync.fulfilled, (state, action) => {
                state.compressionInProgress = false;
                if (action.payload && state.pdfFile) {
                    state.pdfFile.compressedSize = action.payload.compressedSize;
                    state.pdfFile.compressionRatio = action.payload.compressionRatio;
                }
            })
            .addCase(compressPdfAsync.rejected, (state, action) => {
                state.compressionInProgress = false;
                state.error = action.error.message || 'Failed to compress PDF';
            });
    },
});

export const addPdfFileAsync = createAsyncThunk(
    "pdfCompression/addPdfFileAsync",
    async (file: File) => {
        const analyticsService = container.resolve(AnalyticsService);
        analyticsService.LogEvent({
            category: "Button Click",
            action: "Load PDF file",
            label: "PDF Compression",
        });

        if (file.type !== 'application/pdf') {
            throw new Error('Please select a valid PDF file');
        }

        const objectUrl = URL.createObjectURL(file);
        return {
            objectUrl,
            name: file.name,
            file,
            originalSize: file.size,
        } as PdfFile;
    }
);

export const compressPdfAsync = createAsyncThunk<
    { compressedSize: number; compressionRatio: number },
    void,
    { state: RootState }
>(
    "pdfCompression/compressPdfAsync",
    async (_, { getState, dispatch }) => {
        const analyticsService = container.resolve(AnalyticsService);
        analyticsService.LogEvent({
            category: "Button Click",
            action: "Compress PDF",
            label: "PDF Compression",
        });

        const state = getState().pdfCompression;
        
        if (!state.pdfFile) {
            throw new Error('No PDF file selected');
        }

        // Lazy-load PDF compression library
        const { PDFDocument } = await import('pdf-lib');
        
        try {
            // Read the PDF file
            const arrayBuffer = await state.pdfFile.file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            
            // Get compression settings based on quality
            const compressionSettings = getCompressionSettings(state.compressionQuality);
            
            // Save with compression
            const compressedBytes = await pdfDoc.save({
                useObjectStreams: compressionSettings.useObjectStreams,
                addDefaultPage: false,
                objectsPerTick: compressionSettings.objectsPerTick,
                updateFieldAppearances: false,
            });

            const compressedSize = compressedBytes.length;
            const compressionRatio = ((state.pdfFile.originalSize - compressedSize) / state.pdfFile.originalSize) * 100;

            return {
                compressedSize,
                compressionRatio,
            };
        } catch (error) {
            throw new Error('Failed to compress PDF: ' + (error as Error).message);
        }
    }
);

export const downloadCompressedPdfAsync = createAsyncThunk<
    void,
    void,
    { state: RootState }
>(
    "pdfCompression/downloadCompressedPdfAsync",
    async (_, { getState }) => {
        const analyticsService = container.resolve(AnalyticsService);
        analyticsService.LogEvent({
            category: "Button Click",
            action: "Download Compressed PDF",
            label: "PDF Compression",
        });

        const state = getState().pdfCompression;
        
        if (!state.pdfFile) {
            throw new Error('No PDF file selected');
        }

        try {
            // Lazy-load PDF compression library
            const { PDFDocument } = await import('pdf-lib');
            
            const arrayBuffer = await state.pdfFile.file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            
            const compressionSettings = getCompressionSettings(state.compressionQuality);
            
            const compressedBytes = await pdfDoc.save({
                useObjectStreams: compressionSettings.useObjectStreams,
                addDefaultPage: false,
                objectsPerTick: compressionSettings.objectsPerTick,
                updateFieldAppearances: false,
            });

            const blob = new Blob([compressedBytes as BlobPart], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `compressed_${state.pdfFile.name}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            throw new Error('Failed to download PDF: ' + (error as Error).message);
        }
    }
);

function getCompressionSettings(quality: CompressionQuality) {
    switch (quality) {
        case CompressionQuality.low:
            return {
                useObjectStreams: true,
                objectsPerTick: 50,
            };
        case CompressionQuality.medium:
            return {
                useObjectStreams: true,
                objectsPerTick: 20,
            };
        case CompressionQuality.high:
            return {
                useObjectStreams: true,
                objectsPerTick: 10,
            };
        default:
            return {
                useObjectStreams: true,
                objectsPerTick: 20,
            };
    }
}

export const { setCompressionQuality, clearError, resetState } = PdfCompressionSlice.actions;

export const selectPdfCompressionState = (state: RootState) => state.pdfCompression;

export default PdfCompressionSlice.reducer;
